import React, { useEffect, useState, type JSX } from 'react';
import BarcodeScannerComponent from 'react-qr-barcode-scanner';
import { useHistory } from 'react-router-dom';

import { BASE_URL } from '../QRCodeWrapper/QRCodeWrapper';
import getCode from '../../utils/getCode';
import { extractScannedText } from './extractScannedText';

export interface Props {
  closeModal: () => void;
}

type CameraError = 'denied' | 'unsupported' | 'insecure';

const Scanner: React.FC<Props> = ({ closeModal }: Props): JSX.Element => {
  let history = useHistory();

  const [scannedResult, setScannedResult] =
    useState<string>('Scan something....');
  const [cameraError, setCameraError] = useState<CameraError | null>(null);

  // Detect the two failures that never even reach getUserMedia's error
  // callback: a non-secure context (camera APIs are gated to https) and
  // browsers/webviews (e.g. some in-app browsers) that don't expose the API.
  useEffect(() => {
    if (typeof window !== 'undefined' && window.isSecureContext === false) {
      setCameraError('insecure');
    } else if (
      typeof navigator === 'undefined' ||
      !navigator.mediaDevices ||
      typeof navigator.mediaDevices.getUserMedia !== 'function'
    ) {
      setCameraError('unsupported');
    }
  }, []);

  // onUpdate only ever reports successful decodes (its error argument is a
  // per-frame ZXing decode "miss" while no QR is in frame — never a camera
  // failure), so we act on the result alone and ignore the miss.
  const handleScanResult = (_error: unknown, result?: unknown): void => {
    if (!result) return;
    // The ZXing Result is an OBJECT — read its decoded text via getText().
    const text = extractScannedText(result);
    if (!text) return;
    setScannedResult(text);
    if (text.substring(0, BASE_URL.length) === BASE_URL) {
      const code = text.substring(BASE_URL.length);
      closeModal();
      getCode(code, history);
    }
  };

  // Real camera failures arrive here via react-webcam's onUserMediaError
  // (permission denied, no device, hardware busy, insecure context). The
  // argument may be a DOMException, an Error, or a string depending on the
  // browser — but any invocation means the camera could not start, so we
  // always surface the blocked-camera UI.
  const handleCameraError = (_error: unknown): void => {
    setCameraError('denied');
  };

  if (cameraError) {
    const message =
      cameraError === 'insecure'
        ? "Your camera needs a secure (https) connection, so we can't scan here."
        : cameraError === 'unsupported'
          ? "This browser won't let us open the camera. If you're in an app like Instagram or Facebook, open this page in Safari or Chrome — or just type your code."
          : "We couldn't reach your camera. It may be blocked, in use, or permission was declined. You can type your code instead.";

    return (
      <div className="Scanner_error bg-mt-surface flex w-72 flex-col gap-4 rounded-2xl p-6 text-center shadow-2xl">
        <h2 className="text-mt-ink-soft text-lg font-bold tracking-tight">
          Camera unavailable
        </h2>
        <p className="text-mt-ink-muted text-sm">{message}</p>
        <button
          type="button"
          onClick={() => history.push('/enter-code')}
          className="mt-btn bg-mt-primary text-mt-on-primary hover:bg-mt-primary-hover focus-visible:outline-mt-primary inline-flex items-center justify-center gap-x-2 rounded-md px-3.5 py-2.5 text-sm font-semibold shadow-xs focus-visible:outline-2 focus-visible:outline-offset-2"
        >
          Type in a code instead
        </button>
      </div>
    );
  }

  return (
    <>
      <BarcodeScannerComponent
        facingMode="environment"
        onUpdate={handleScanResult}
        onError={handleCameraError}
      />
      <p>{scannedResult}</p>
    </>
  );
};

export default Scanner;
